export const $$ = document.querySelectorAll.bind(document);
export const $ = document.querySelector.bind(document);

export function parseNum(value) {
  if (value === null || value === undefined) return 0;
  const number = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(number) ? number : 0;
}

export function bindSsrHeaderAction(button, boundKey, action) {
  if (!button || button.dataset[boundKey] === "1") return;
  button.dataset[boundKey] = "1";
  button.addEventListener("click", action);
}

const STANDARD_SAVE_PALETTE_CLASSES = {
  save: [
    "bg-emerald-600",
    "text-white",
    "ring-1",
    "ring-emerald-700",
    "hover:bg-emerald-500",
  ],
  update: ["bg-blue-600", "text-white", "hover:bg-blue-700"],
};
const STANDARD_SAVE_DISABLED_CLASSES = [
  "bg-gray-300",
  "text-gray-500",
  "cursor-not-allowed",
];
const STANDARD_SAVE_MANAGED_CLASSES = [
  ...STANDARD_SAVE_PALETTE_CLASSES.save,
  ...STANDARD_SAVE_PALETTE_CLASSES.update,
  "bg-indigo-600",
  "bg-indigo-700",
  "ring-indigo-700",
  "hover:bg-indigo-500",
  "hover:bg-indigo-700",
  ...STANDARD_SAVE_DISABLED_CLASSES,
];

export function setStandardSaveButtonState(button, options = {}) {
  if (!button) return;
  const palette = options.palette || button.dataset.savePalette || "save";
  const isUpdate = palette === "update";
  const disabled = Boolean(options.disabled);

  button.dataset.savePalette = isUpdate ? "update" : "save";
  button.disabled = disabled;
  button.classList.remove(...STANDARD_SAVE_MANAGED_CLASSES);
  button.classList.add(
    ...(disabled
      ? STANDARD_SAVE_DISABLED_CLASSES
      : STANDARD_SAVE_PALETTE_CLASSES[isUpdate ? "update" : "save"]),
  );
}

export async function fetchWithError(url, options, message = "Request failed. Please try again.") {
  try {
    const response = await fetch(url, options);
    if (response.ok) return response;
    throw new Error(`${message} (${response.status})`);
  } catch (error) {
    if (typeof window.showFlashMessage === "function") {
      window.showFlashMessage(null, "error", message, 5);
    }
    throw error;
  }
}

export const CLOSE_BUTTON_CLASS = [
  "smaac-close-btn",
  "ml-auto",
  "inline-flex",
  "h-8",
  "w-8",
  "shrink-0",
  "items-center",
  "justify-center",
  "rounded-full",
  "bg-transparent",
  "p-0",
  "text-sm",
  "font-semibold",
  "leading-none",
  "text-red-500",
  "transition-colors",
  "hover:bg-red-100",
  "hover:text-red-600",
  "focus:outline-none",
  "focus:ring-2",
  "focus:ring-red-200",
].join(" ");

const DATE_FILTER_FIELDS = ["period", "start_date", "end_date"];
const DATE_FILTER_STORAGE_PREFIX = "smaac:ssr:date-filter:v1";
const DATE_RANGE_GUARD_SELECTOR = `:is(${[
  '[data-needs-init="initDateRangeGuard"]',
  '[data-needs-init-started*="initDateRangeGuard"]',
  '[data-needs-init-completed*="initDateRangeGuard"]',
].join(", ")})`;

export function createCloseButton(options) {
  const opts = options || {};
  const button = document.createElement("button");
  button.type = "button";
  button.className = [CLOSE_BUTTON_CLASS, opts.className || ""].filter(Boolean).join(" ");
  button.setAttribute("aria-label", opts.ariaLabel || "Close");
  button.textContent = "×";
  if (typeof opts.onClick === "function") {
    button.onclick = opts.onClick;
  }
  return button;
}

function _closest(el, selector) {
  try {
    return el && el.closest ? el.closest(selector) : null;
  } catch (_e) {
    return null;
  }
}

function _dateFilterStorageScope() {
  const body = document.body || {};
  return [
    body.dataset?.ccode || "",
    body.dataset?.userAccountId || "",
  ].join(":");
}

function _dateFilterStorageKey(slug) {
  return [DATE_FILTER_STORAGE_PREFIX, _dateFilterStorageScope(), slug || ""].join(":");
}

function _readDateFilter(slug) {
  if (!slug || !window.localStorage) return null;

  let raw = "";
  try {
    raw = window.localStorage.getItem(_dateFilterStorageKey(slug));
  } catch (_error) {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      period: typeof parsed.period === "string" ? parsed.period : "",
      start_date: typeof parsed.start_date === "string" ? parsed.start_date : "",
      end_date: typeof parsed.end_date === "string" ? parsed.end_date : "",
    };
  } catch (_error) {
    return null;
  }
}

function _writeDateFilter(slug, values) {
  if (!slug || !window.localStorage) return;
  const payload = {};
  DATE_FILTER_FIELDS.forEach((name) => {
    payload[name] = typeof values?.[name] === "string" ? values[name] : "";
  });

  try {
    window.localStorage.setItem(_dateFilterStorageKey(slug), JSON.stringify(payload));
  } catch (_error) {
    // Browser storage failure should not block date filtering.
  }
}

function _getDateFilterControls(slug, root) {
  root = root || document;
  if (!slug || !window.CSS || !CSS.escape) return {};

  const selector = `[data-formname="${CSS.escape(slug)}"]`;
  const controls = {};
  DATE_FILTER_FIELDS.forEach((name) => {
    const seen = new Set();
    const candidates = [];
    [root, document].forEach((scope) => {
      if (!scope || !scope.querySelectorAll) return;
      scope.querySelectorAll(`${selector}[name="${name}"]`).forEach((control) => {
        if (seen.has(control)) return;
        seen.add(control);
        candidates.push(control);
      });
    });
    controls[name] = candidates.find((control) => (control.type || "").toLowerCase() !== "hidden")
      || candidates[0]
      || null;
  });
  return controls;
}

function _getDateFilterValues(slug, root) {
  const controls = _getDateFilterControls(slug, root);
  const values = {};
  DATE_FILTER_FIELDS.forEach((name) => {
    values[name] = controls[name]?.value || "";
  });
  return values;
}

function _setDateFilterValues(slug, values, root) {
  const controls = _getDateFilterControls(slug, root);
  let changed = false;
  DATE_FILTER_FIELDS.forEach((name) => {
    const control = controls[name];
    if (!control || typeof values?.[name] !== "string") return;
    if (!values[name] && control.value) return;
    if (control.value === values[name]) return;
    control.value = values[name];
    changed = true;
  });
  return changed;
}

function _slugFromSsrPath(pathname) {
  const match = String(pathname || "").match(/^\/ssr\/([^/?#]+)\//);
  return match ? decodeURIComponent(match[1]) : "";
}

function _urlHasDateFilterParams(url) {
  return DATE_FILTER_FIELDS.some((name) => url.searchParams.has(name));
}

function _stripDateFilterParamsFromPath(path) {
  if (typeof path !== "string" || !DATE_FILTER_FIELDS.some((name) => path.indexOf(name + "=") !== -1)) {
    return path;
  }

  try {
    const url = new URL(path, window.location.origin);
    DATE_FILTER_FIELDS.forEach((name) => {
      url.searchParams.delete(name);
    });
    return url.pathname + url.search + (url.hash || "");
  } catch (_error) {
    return path;
  }
}

function _stripDateFilterParamsFromHtmxDetail(detail) {
  if (!detail) return;
  detail.path = _stripDateFilterParamsFromPath(detail.path);
  if (detail.requestConfig) {
    detail.requestConfig.path = _stripDateFilterParamsFromPath(detail.requestConfig.path);
  }
  if (detail.pathInfo) {
    detail.pathInfo.requestPath = _stripDateFilterParamsFromPath(detail.pathInfo.requestPath);
  }
}

function _cleanDateFilterControlRequestUrl(control) {
  if (!control || !control.getAttribute || !control.setAttribute) return;
  ["hx-get", "data-hx-get"].forEach((attr) => {
    const value = control.getAttribute(attr);
    const nextValue = _stripDateFilterParamsFromPath(value);
    if (nextValue && nextValue !== value) control.setAttribute(attr, nextValue);
  });
}

function _getDateFilterRequestValues(slug, source) {
  const values = _getDateFilterValues(slug);
  if (source && source.name === "period") {
    values.period = source.value || "";
    if (values.period && values.period !== "customrange") {
      values.start_date = "";
      values.end_date = "";
    }
  } else if (source && source.name === "start_date") {
    values.start_date = source.value || "";
    values.period = "customrange";
  } else if (source && source.name === "end_date") {
    values.end_date = source.value || "";
    values.period = "customrange";
  }
  return values;
}

function _setDateFilterPeriod(slug, period, root) {
  const periodControl = _getDateFilterControls(slug, root).period;
  if (periodControl && periodControl.value !== period) {
    periodControl.value = period;
  }
}

function _setDateFilterRequestParams(event, slug, source) {
  const detail = event && event.detail;
  const params = detail && detail.parameters;
  if (!params) return;

  _normalizeShortYear(source);
  if (source && (source.name === "start_date" || source.name === "end_date")) {
    _setDateFilterPeriod(slug, "customrange");
  }
  _cleanDateFilterControlRequestUrl(source);
  const sourcePath = source && source.getAttribute
    ? source.getAttribute("hx-get") || source.getAttribute("data-hx-get")
    : "";
  if (sourcePath) {
    detail.path = _stripDateFilterParamsFromPath(sourcePath);
  }
  _stripDateFilterParamsFromHtmxDetail(detail);
  const values = _getDateFilterRequestValues(slug, source);
  [params, detail.unfilteredParameters].forEach((target) => {
    if (!target) return;
    DATE_FILTER_FIELDS.forEach((name) => {
      if (values[name]) target[name] = values[name];
      else delete target[name];
    });
  });
  _writeDateFilter(slug, values);
}

function _normalizeShortYear(input) {
  if (!input) return;
  const value = input.value;
  if (!value) return;
  const parts = value.split("-");
  if (parts.length !== 3) return;
  const year = parseInt(parts[0], 10);
  if (Number.isNaN(year) || year >= 1000) return;

  const currYear = parseInt(todayIsoDate().slice(0, 4), 10);
  const currCentury = Math.floor(currYear / 100) * 100;
  let candidate = currCentury + year;
  while (candidate - currYear > 50) candidate -= 100;
  while (candidate - currYear < -50) candidate += 100;

  const normalized = String(candidate).padStart(4, "0") + "-" + parts[1] + "-" + parts[2];
  if (normalized !== input.value) input.value = normalized;
}

function _clampDateInputToMax(input) {
  if (!input) return;
  _normalizeShortYear(input);
  if (input.value && input.max && input.value > input.max) {
    input.value = input.max;
    showFutureDateError();
  }
}

function _clearInvalidDateRange(input) {
  if (!input || (input.name !== "start_date" && input.name !== "end_date")) return false;
  const controls = _getDateFilterControls(input.dataset.formname);
  if (!controls.start_date?.value || !controls.end_date?.value
    || controls.start_date.value <= controls.end_date.value) return false;

  input.value = "";
  _writeDateFilter(input.dataset.formname, _getDateFilterRequestValues(input.dataset.formname, input));
  window.showFlashMessage?.(
    null,
    "warning",
    input.name === "start_date"
      ? "Start Date cannot be greater than End Date."
      : "End Date cannot be less than Start Date.",
    5,
  );
  return true;
}

let _shortYearBlurGuardBound = false;
function _installShortYearBlurGuard() {
  if (_shortYearBlurGuardBound) return;
  _shortYearBlurGuardBound = true;

  const isDateRangeInput = function (input) {
    return input?.matches?.(`${DATE_RANGE_GUARD_SELECTOR}[type="date"]`);
  };

  document.addEventListener("blur", function (event) {
    const input = event.target;
    if (!isDateRangeInput(input)) return;
    const valueBeforeNormalization = input.value;
    _clampDateInputToMax(input);
    if (input.value !== valueBeforeNormalization) {
      window.setTimeout(() => window.htmx?.trigger(input, "blur"), 0);
    }
  }, true);
}

function _handleCustomRangeSelectBeforeRequest(select, event) {
  if (!select || select.value !== "customrange" || select.dataset.daterangeKeepDefaults === "true") return false;

  event.preventDefault();
  [select.dataset.daterangeStartId, select.dataset.daterangeEndId].forEach((id) => {
    if (!id) return;
    const input = document.getElementById(id);
    if (input) input.value = "";
  });

  _cleanDateFilterControlRequestUrl(select);
  _writeDateFilter(select.dataset.formname, {
    period: "customrange",
    start_date: "",
    end_date: "",
  });
  return true;
}

function _isDateFilterControl(control) {
  if (!control || !control.matches) return false;
  return control.matches(`select[name="period"][data-formname], ${DATE_RANGE_GUARD_SELECTOR}[data-formname]`);
}

let _dateFilterRequestSyncBound = false;
function _installDateFilterRequestSync() {
  if (_dateFilterRequestSyncBound) return;
  _dateFilterRequestSyncBound = true;

  document.addEventListener("htmx:configRequest", function (event) {
    const source = event.detail && event.detail.elt;
    if (!_isDateFilterControl(source)) return;
    _setDateFilterRequestParams(event, source.dataset.formname, source);
  }, true);
}

export function addStoredDateFilterToUrl(url) {
  if (!url || _urlHasDateFilterParams(url)) return url;
  const slug = _slugFromSsrPath(url.pathname);
  const values = _readDateFilter(slug);
  if (!values) return url;

  DATE_FILTER_FIELDS.forEach((name) => {
    if (values[name]) url.searchParams.set(name, values[name]);
  });
  return url;
}

function _safeFieldSelector(attr, value) {
  if (!attr || !value) return "";
  if (typeof CSS !== "undefined" && CSS.escape) {
    return `[${attr}="${CSS.escape(value)}"]`;
  }
  return `[${attr}="${String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"]`;
}

function _findFieldShell(name, root) {
  root = root || document;
  if (!name) return null;

  const idSelector = _safeFieldSelector("id", name);
  const nameSelector = _safeFieldSelector("name", name);
  const el = (idSelector && root.querySelector(idSelector))
    || (nameSelector && root.querySelector(nameSelector));
  if (!el) return null;

  if (el.matches && el.matches('input[type="checkbox"]')) {
    return _closest(el, "fieldset") || el;
  }

  return _closest(el, ".relative.my-2")
    || _closest(el, '[data-select-type="select"]')
    || _closest(el, ".relative.mx-1")
    || _closest(el, "fieldset")
    || el;
}

function _setVirtualSelectEmpty(el) {
  if (!el) return;
  if (typeof el.setValueSilently === "function") {
    el.setValueSilently("");
  } else if (typeof el.setValue === "function") {
    el.setValue("", true);
  } else if ("value" in el) {
    el.value = "";
  }
  if (el.dataset) el.dataset.saveddata = "";
}

export function clearFormFieldValue(name, root, emitChange = true) {
  root = root || document;
  if (!name) return;

  const idSelector = _safeFieldSelector("id", name);
  const nameSelector = _safeFieldSelector("name", name);
  const hiddenIdSelector = _safeFieldSelector("id", `${name}_input`);
  const fields = new Set();

  [idSelector, nameSelector, hiddenIdSelector].forEach((selector) => {
    if (!selector || !root.querySelectorAll) return;
    root.querySelectorAll(selector).forEach((field) => fields.add(field));
  });

  fields.forEach((field) => {
    const type = (field.type || "").toLowerCase();
    if (field.dataset && field.dataset.init === "virtual-select") {
      _setVirtualSelectEmpty(field);
    } else if (type === "checkbox" || type === "radio") {
      field.checked = false;
    } else if ("value" in field) {
      field.value = "";
      if (field.matches?.("[data-richtext-source]")) {
        const editor = field.closest("[data-richtext-root]")?.querySelector("[data-richtext-editor]");
        if (editor) editor.textContent = "";
      }
    } else if ((field.tagName === "SPAN" || field.tagName === "P") && !field.querySelector("input, select, textarea")) {
      field.textContent = "";
    }
    if (field.dataset) field.dataset.saveddata = "";
    if (field.matches?.("[data-toggleable-radio-group]")) field.dataset.checked = "";
    if (emitChange) field.dispatchEvent(new Event("change", { bubbles: true }));
  });
}

function _isClearableFieldShell(el) {
  if (!el || !el.matches) return false;
  return el.matches(".relative.my-2, .relative.mx-1, [data-select-type='select']")
    || (el.matches("fieldset") && !!el.querySelector('input[type="checkbox"], input[type="radio"]'));
}

function _isHiddenForFieldClear(el) {
  if (!el || !el.classList) return false;
  return el.classList.contains("hidden") || (el.style && el.style.display === "none");
}

function _clearableFieldShellsIn(el) {
  if (!el) return [];
  if (_isClearableFieldShell(el)) return [el];
  if (!el.querySelectorAll) return [];
  return Array.from(el.querySelectorAll(".relative.my-2, .relative.mx-1, [data-select-type='select'], fieldset"))
    .filter(_isClearableFieldShell);
}

function _fieldNamesInShell(shell) {
  const names = new Set();
  if (!shell || !shell.querySelectorAll) return names;

  shell.querySelectorAll("input, select, textarea, [data-init='virtual-select'][id]").forEach((field) => {
    if (!field) return;
    if (field.dataset && field.dataset.smaacDoNotClearOnHide === "1") return;
    const name = field.dataset && field.dataset.init === "virtual-select"
      ? field.id
      : field.name || field.id || "";
    if (!name || name.endsWith("_input")) return;
    names.add(name);
  });
  return names;
}

function _clearHiddenFieldShell(shell) {
  if (!_isHiddenForFieldClear(shell)) return;
  // Alpine-managed elements toggle display via x-show / x-cloak. Their hidden
  // state is a transient visibility flip (e.g. an inactive tab), not a real
  // "field was removed from the form" signal — never wipe field values here.
  if (shell.hasAttribute && (
      shell.hasAttribute("x-show")
      || shell.hasAttribute("x-cloak")
      || shell.hasAttribute("x-if")
  )) return;
  const clearableShells = _clearableFieldShellsIn(shell);
  if (clearableShells.length !== 1) return;
  const fieldShell = clearableShells[0];
  const scope = _closest(shell, "form") || _closest(shell, "[data-formname]") || shell.ownerDocument || document;
  _fieldNamesInShell(fieldShell).forEach((name) => clearFormFieldValue(name, scope));
}

export function toggleFormField(name, show, options) {
  const opts = options || {};
  const root = opts.root || document;
  const shell = _findFieldShell(name, root);
  if (shell) shell.classList.toggle("hidden", !show);
  if (!show && opts.clearOnHide !== false) {
    clearFormFieldValue(name, root);
  }
  return shell;
}

let _hiddenFieldClearObserverInstalled = false;
export function initHiddenFieldValueClear(root) {
  if (_hiddenFieldClearObserverInstalled || typeof MutationObserver === "undefined") return;
  _hiddenFieldClearObserverInstalled = true;
  const scope = root || document;
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type !== "attributes" || !["class", "style"].includes(mutation.attributeName)) return;
      _clearHiddenFieldShell(mutation.target);
    });
  });
  observer.observe(scope.documentElement || scope.body || scope, {
    attributes: true,
    attributeFilter: ["class", "style"],
    subtree: true,
  });
}

if (typeof window !== "undefined") {
  // Plain SSR form bundles use global hooks instead of ES module imports.
  window.smaacClearFormFieldValue = clearFormFieldValue;
  window.smaacToggleFormField = toggleFormField;
  window.smaacInitHiddenFieldValueClear = initHiddenFieldValueClear;
  initHiddenFieldValueClear();
}

function _getBaseFieldName(el) {
  if (!el) return null;

  // Standard input/select/textarea patterns
  const nameAttr = el.getAttribute ? el.getAttribute("name") : null;
  if (nameAttr) return nameAttr;

  const idAttr = el.getAttribute ? el.getAttribute("id") : null;
  if (idAttr) {
    if (idAttr.endsWith("_input")) return idAttr.slice(0, -6);
    return idAttr;
  }

  // VirtualSelect: value changes fire on the div[data-init="virtual-select"]
  const vs = _closest(el, '[data-init="virtual-select"]');
  if (vs && vs.id) return vs.id;

  return null;
}

function _getFieldCurrentValue(baseName, root) {
  root = root || document;
  if (!baseName) return "";

  // Prefer explicit matches within the same form/container
  const candidates = [];
  try {
    candidates.push(root.querySelector(`[name="${CSS.escape(baseName)}"]`));
  } catch (_e) {
    // ignore (older browsers / bad names)
  }
  candidates.push(root.getElementById ? root.getElementById(baseName) : null);
  candidates.push(root.getElementById ? root.getElementById(`${baseName}_input`) : null);

  const el = candidates.find(Boolean);
  if (!el) return "";

  // VirtualSelect container div has `.value`
  if (el.dataset && el.dataset.init === "virtual-select") {
    return el.value ?? "";
  }

  // Checkbox / radio
  const type = (el.getAttribute && el.getAttribute("type")) || "";
  if (type === "checkbox" || type === "radio") {
    return el.checked ? "1" : "";
  }

  return (el.value ?? "").toString();
}

function _shouldClearFeedback(feedbackEl) {
  if (!feedbackEl) return false;
  const txt = (feedbackEl.textContent || "").toLowerCase();
  if (txt.includes("required")) return true;
  if (feedbackEl.querySelector && feedbackEl.querySelector(".invalid-feedback")) return true;
  return false;
}

function _clearErrorStylingForField(baseName, root) {
  root = root || document;
  if (!baseName) return;

  const nodes = [];
  // Standard input/select/textarea
  try {
    nodes.push(root.querySelector(`[name="${CSS.escape(baseName)}"]`));
  } catch (_e) {
    // ignore
  }
  nodes.push(root.getElementById ? root.getElementById(baseName) : null);
  nodes.push(root.getElementById ? root.getElementById(`${baseName}_input`) : null);

  // VirtualSelect toggle button, if present
  const vs = root.getElementById ? root.getElementById(baseName) : null;
  if (vs && vs.querySelector) {
    nodes.push(vs.querySelector(".vscomp-toggle-button"));
  }

  nodes.filter(Boolean).forEach((el) => {
    try {
      el.classList.remove(
        "ssr-missing-required",
        "border-red-500",
        "ring-red-500",
        "bg-red-50",
        "smaac-invalid",
      );
      el.removeAttribute("aria-invalid");
    } catch (_e) {
      // ignore
    }
  });
}

function _clearRequiredFeedbackForField(baseName, root) {
  root = root || document;
  if (!baseName) return;

  const feedbackId = `${baseName}-feedback`;
  const feedbackEl = root.getElementById ? root.getElementById(feedbackId) : null;
  if (!feedbackEl) return;
  if (!_shouldClearFeedback(feedbackEl)) return;

  const val = _getFieldCurrentValue(baseName, root);
  const hasValue = String(val ?? "").trim() !== "";
  if (!hasValue) return;

  try {
    feedbackEl.innerHTML = "";
    feedbackEl.classList.add("hidden");
  } catch (_e) {
    // ignore
  }
  _clearErrorStylingForField(baseName, root);
}

export function initAutoClearRequired(root) {
  root = root || document;

  // Install global handlers once per page load.
  if (document.documentElement && document.documentElement.dataset.autoClearRequiredInstalled === "1") {
    return;
  }
  if (document.documentElement) {
    document.documentElement.dataset.autoClearRequiredInstalled = "1";
  }

  function onAnyFieldChange(evt) {
    const target = evt && evt.target ? evt.target : null;
    const baseName = _getBaseFieldName(target);
    if (!baseName) return;

    // Use nearest form/container when available to avoid collisions.
    const scope = _closest(target, "form") || document;
    _clearRequiredFeedbackForField(baseName, scope);
  }

  // Capture phase so we see events even if components stop propagation.
  document.addEventListener("input", onAnyFieldChange, true);
  document.addEventListener("change", onAnyFieldChange, true);

  // Best-effort: clear any stale "required" messages where value is already present.
  try {
    const feedbackNodes = (root.querySelectorAll && root.querySelectorAll('[id$="-feedback"]')) || [];
    feedbackNodes.forEach((fb) => {
      if (!_shouldClearFeedback(fb)) return;
      const id = fb.getAttribute("id") || "";
      const baseName = id.endsWith("-feedback") ? id.slice(0, -9) : null;
      if (!baseName) return;
      _clearRequiredFeedbackForField(baseName, root);
    });
  } catch (_e) {
    // ignore
  }
}

export function initTimeClear(root) {
  root = root || document;
  root.querySelectorAll('[data-needs-init="initTimeClear"]').forEach(function(container) {
    if (container.dataset.timeClearBound === '1') return;
    container.dataset.timeClearBound = '1';
    const inp = container.querySelector('input[type="time"]');
    const btn = container.querySelector('[data-time-clear]');
    if (!inp || !btn) return;
    function _sync() { btn.classList.toggle('hidden', !inp.value); }
    _sync();
    inp.addEventListener('change', _sync);
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      inp.value = '';
      _sync();
      inp.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

export function dateFilterMax(period) {
  return period?.value === 'thisfiscalyear' && period.dataset.fiscalYearEnd
    ? period.dataset.fiscalYearEnd : todayIsoDate();
}

export function initDateRangeGuard(root) {
  root = root || document;
  _installDateFilterRequestSync();
  _installShortYearBlurGuard();

  const slugs = new Set();
  root
    .querySelectorAll(`select[name="period"][data-formname], ${DATE_RANGE_GUARD_SELECTOR}[data-formname]`)
    .forEach((control) => {
      if (control.dataset.formname) slugs.add(control.dataset.formname);
    });

  const params = new URLSearchParams(window.location.search);
  const hasUrlDateParams = DATE_FILTER_FIELDS.some((name) => params.has(name));

  slugs.forEach((slug) => {
    if (hasUrlDateParams) return;
    const restored = _readDateFilter(slug);
    if (!restored) return;
    const changed = _setDateFilterValues(slug, restored, root);
    if (!changed) return;

    const controls = _getDateFilterControls(slug, root);
    const trigger = controls.period || controls.start_date || controls.end_date;
    if (trigger && trigger.dataset.dateFilterRestoreRequested !== "1") {
      trigger.dataset.dateFilterRestoreRequested = "1";
      window.setTimeout(() => {
        trigger.dispatchEvent(new Event("change", { bubbles: true }));
      }, 0);
    }
  });

  root.querySelectorAll('select[name="period"][data-formname]').forEach(function (select) {
    if (select.dataset.dateFilterPersistenceBound === "1") return;
    select.dataset.dateFilterPersistenceBound = "1";
    const save = function () {
      const controls = _getDateFilterControls(select.dataset.formname, root);
      [controls.start_date, controls.end_date].forEach(input => {
        if (input) input.max = dateFilterMax(select);
      });
      _cleanDateFilterControlRequestUrl(select);
      _writeDateFilter(select.dataset.formname, _getDateFilterRequestValues(select.dataset.formname, select));
    };
    select.addEventListener("change", save, true);
    select.addEventListener("htmx:beforeRequest", function (event) {
      if (_handleCustomRangeSelectBeforeRequest(select, event)) return;
      save();
    });
  });

  const dateInputs = root.querySelectorAll(DATE_RANGE_GUARD_SELECTOR);
  dateInputs.forEach(function (input) {
    if (input.dataset.dateRangeGuardBound === '1') return;
    input.dataset.dateRangeGuardBound = '1';

    const period = _getDateFilterControls(input.dataset.formname, root).period;
    const maxDate = dateFilterMax(period);
    if (!input.max || input.max > maxDate || period?.value === 'thisfiscalyear') {
      input.max = maxDate;
    }
    if (input.value && input.value > input.max) {
      input.value = input.max;
    }

    const clampToMax = function () {
      _clampDateInputToMax(input);
    };

    const save = function () {
      if (input.name === "start_date" || input.name === "end_date") {
        _setDateFilterPeriod(input.dataset.formname, "customrange");
      }
      _cleanDateFilterControlRequestUrl(input);
      _writeDateFilter(input.dataset.formname, _getDateFilterRequestValues(input.dataset.formname, input));
    };

    input.addEventListener("change", save, true);
    input.addEventListener("blur", save, true);

    input.addEventListener("htmx:beforeRequest", function (event) {
      clampToMax();
      if (_clearInvalidDateRange(input)) {
        event.preventDefault();
        event.stopPropagation();
      }
    });

    input.addEventListener("htmx:configRequest", function (event) {
      clampToMax();
      if (input.name === "start_date" || input.name === "end_date") {
        _setDateFilterPeriod(input.dataset.formname, "customrange");
      }
      if (event?.detail?.parameters && input.name) {
        event.detail.parameters[input.name] = input.value;
      }
    });
  });
}

// Applying the date filter only HTMX-swaps content; the address bar keeps the
// unfiltered URL, so a native back (from an opened entry) re-renders with the
// server's default period. Mirror the active filter into the URL on apply — the
// server already rehydrates period/start_date/end_date from the query string.
let _dateFilterUrlSyncBound = false;
export function initDateFilterUrlSync() {
  if (_dateFilterUrlSyncBound) return;
  _dateFilterUrlSyncBound = true;

  document.addEventListener('htmx:afterSettle', (evt) => {
    const trigger = evt.detail?.requestConfig?.elt || evt.detail?.elt;
    const slug = trigger?.getAttribute?.('data-formname');
    if (!slug || !trigger.matches(`select[name="period"], ${DATE_RANGE_GUARD_SELECTOR}`)) return;

    const formSel = `[data-formname="${CSS.escape(slug)}"]`;
    const url = new URL(window.location.href);
    document
      .querySelectorAll(`${formSel}[name="period"], ${formSel}${DATE_RANGE_GUARD_SELECTOR}`)
      .forEach((c) => (c.value ? url.searchParams.set(c.name, c.value) : url.searchParams.delete(c.name)));
    _writeDateFilter(slug, _getDateFilterValues(slug));
    history.replaceState(history.state, '', url.href);
  });
}
const VOUCHER_DATE_FIELDS = new Set(['indentdate', 'podate', 'orderdate']);
const DATE_RESTRICTIONS = new Set(['future', 'past', 'both']);
const DATE_INPUT_SELECTOR = 'input[type="date"], input[type="datetime-local"]';
const SERVER_NOW_LOADED_AT = Date.now();

function dateInput(el) {
  const target = el?.matches?.(DATE_INPUT_SELECTOR)
    ? el
    : document.getElementById(el?.dataset?.element || '');
  return target?.matches?.(DATE_INPUT_SELECTOR) ? target : null;
}

export function todayIsoDate() {
  const serverNow = document.body?.dataset.serverNow;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}$/.test(serverNow || '')) {
    const serverNowMs = Date.parse(serverNow + 'Z');
    if (!Number.isNaN(serverNowMs)) {
      const elapsedMs = Math.max(0, Date.now() - SERVER_NOW_LOADED_AT);
      return new Date(serverNowMs + elapsedMs).toISOString().slice(0, 10);
    }
  }
  const today = new Date();
  return [today.getFullYear(), today.getMonth() + 1, today.getDate()]
    .map((part, index) => index === 0 ? String(part) : String(part).padStart(2, '0'))
    .join('-');
}

export function setDateRestriction(el, category = 'future') {
  const target = dateInput(el);
  if (!target) return;
  const restriction = DATE_RESTRICTIONS.has(category) ? category : 'future';
  if (!target.dataset.dateRestrictionOriginal) {
    target.dataset.dateRestrictionOriginal = JSON.stringify({
      min: target.getAttribute('min'),
      max: target.getAttribute('max'),
    });
  }
  const original = JSON.parse(target.dataset.dateRestrictionOriginal);
  const today = todayIsoDate();
  const restore = (name, value) => {
    if (value == null) target.removeAttribute(name);
    else target.setAttribute(name, value);
  };
  const hasTime = target.type === 'datetime-local';
  restore('min', restriction === 'past' ? today + (hasTime ? 'T00:00' : '') : original.min);
  restore('max', restriction === 'future' ? today + (hasTime ? 'T23:59:59.999' : '') : original.max);
  target.dataset.dateRestriction = restriction;
}

function initNoFutureDate(el) {
  setDateRestriction(el, 'future');
}

function futureDateError(input) {
  return input?.value
    && input.max
    && input.value > input.max;
}

function showFutureDateError() {
  window.showFlashMessage?.(null, 'warning', "Future dates can't be selected", 5);
}

let _futureDateRequestGuardBound = false;
function bindFutureDateRequestGuard() {
  if (_futureDateRequestGuardBound) return;
  _futureDateRequestGuardBound = true;
  const correctFutureDate = (event) => {
    const input = event.target;
    if (!input?.matches?.(DATE_INPUT_SELECTOR)) return;
    if (input.dataset.dateCategory === 'future' || VOUCHER_DATE_FIELDS.has(input.name)) {
      setDateRestriction(input, 'future');
    }
    if (!futureDateError(input)) return;
    // Keep the selected time when correcting a future voucher day.
    const corrected = input.type === 'datetime-local'
      ? input.max.slice(0, 10) + input.value.slice(10)
      : input.max;
    input.value = corrected > input.max ? input.max : corrected;
    input.setCustomValidity('');
    showFutureDateError();
    if (event.type !== 'change') input.dispatchEvent(new Event('change', { bubbles: true }));
  };
  // Native pickers can commit another selection while the input retains focus.
  document.addEventListener('change', correctFutureDate, true);
  document.addEventListener('focusout', correctFutureDate, true);
  document.addEventListener('htmx:beforeRequest', (event) => {
    const source = event.detail?.elt;
    if (!source?.getAttribute?.('hx-post')) return;
    const form = source?.closest?.('form') || source?.closest?.('[data-formname]');
    const invalid = [...(form?.querySelectorAll?.('input[data-date-restriction="future"]') || [])]
      .find(futureDateError);
    if (!futureDateError(invalid)) return;
    event.preventDefault();
    invalid.setCustomValidity('Voucher date cannot be greater than current date.');
    invalid.reportValidity();
    showFutureDateError();
  }, true);
}
bindFutureDateRequestGuard();

function applyDateRestrictions(root) {
  (root || document).querySelectorAll(DATE_INPUT_SELECTOR).forEach((input) => {
    const category = input.dataset.dateCategory || input.dataset.dateRestriction
      || (VOUCHER_DATE_FIELDS.has(input.name) ? 'future' : null);
    if (!category) return;
    setDateRestriction(input, category);
    if (input.dataset.futureDateValidationBound === '1') return;
    input.dataset.futureDateValidationBound = '1';
    const validate = () => input.setCustomValidity(
      futureDateError(input) ? 'Voucher date cannot be greater than current date.' : ''
    );
    input.addEventListener('input', validate);
    input.addEventListener('change', validate);
    validate();
  });
}

window.initNoFutureDate = initNoFutureDate;
window.setDateRestriction = setDateRestriction;
document.addEventListener('DOMContentLoaded', () => {
  applyDateRestrictions();
  document.body.addEventListener('htmx:afterSwap', (event) => applyDateRestrictions(event.target));
});
